/**
 * PHARMAKON — display-preferences panel (« Préférences d'affichage »)
 *
 * Keeps only Thème and Densité (design/ux/01_foundations.md §2.9): the accent
 * picker is retired (AUD-UXDS-14 — it recoloured half the UI) and the theme
 * choices are the three Novera Pro options of `THEME_OPTIONS`.
 *
 * Dev role switcher: rendered only in a development build
 * (`import.meta.env.DEV`). It mutates the cached user.role in authStore
 * (in-memory only) so designers can verify role-gated UI without
 * re-authenticating; the server still enforces the real role on every call.
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import {
  Checkbox,
  Icon,
  IconButton,
  Label,
  Select,
} from "@/components/ui/primitives";
import { SCAN_GAP_OPTIONS } from "@/config/scanner.config.js";
import { readScannerPrefs, writeScannerPrefs } from "@/lib/scan-detector.js";
import { LOCALE_LABELS } from "@/config/locale.config.js";
import { THEME_OPTIONS, DENSITY_OPTIONS } from "@/config/ui.config.js";
import { useAuthStore } from "@/stores/authStore.js";
import { useThemeStore } from "@/stores/themeStore.js";

/** Dev-only role override targets (never rendered in a production build). */
const DEV_ROLES = [
  "PROPRIETAIRE",
  "MANAGER",
  "CAISSIER",
  "FOURNISSEUR",
  "ADMIN_PHARMAKON",
] as const;

const SHOW_DEV_ROLE_SWITCHER = Boolean(import.meta.env.DEV);

export const TweaksPanel: React.FC = () => {
  const showTweaks = useThemeStore((s) => s.showTweaks);
  const setShowTweaks = useThemeStore((s) => s.setShowTweaks);
  const themePref = useThemeStore((s) => s.themePref);
  const density = useThemeStore((s) => s.density);
  const setTheme = useThemeStore((s) => s.setTheme);
  const setDensity = useThemeStore((s) => s.setDensity);
  const navHoverOpen = useThemeStore((s) => s.navHoverOpen);
  const setNavHoverOpen = useThemeStore((s) => s.setNavHoverOpen);

  const user = useAuthStore((s: any) => s.user);
  // Barcode scanner — per-workstation (ux/01 §4.8, OP-ACH-25: beep off by
  // default; a slower scanner raises the inter-key gap).
  const [scanner, setScanner] = React.useState(() => readScannerPrefs());
  const updateScanner = (patch: { beep?: boolean; maxGapMs?: number }) => {
    setScanner(writeScannerPrefs(patch));
    window.dispatchEvent(new Event("pharmakon:scanner-prefs"));
  };

  if (!showTweaks) return null;

  // In-memory role override for design verification (scope #3 — server still
  // enforces the real role). Persists for the session only.
  const switchRole = (role: string) => {
    useAuthStore.setState((s: any) => ({
      ...s,
      user: s.user ? { ...s.user, role } : s.user,
    }));
  };

  return (
    <div
      role="dialog"
      aria-label="Préférences d'affichage"
      className="fixed bottom-4 right-4 w-[320px] max-w-[calc(100vw-2rem)] bg-bg-elevated border border-border-default rounded-xl shadow-pop z-[60] overflow-hidden"
      style={{
        animation: "modal-pop 200ms cubic-bezier(0.16,1,0.3,1)",
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle bg-bg-sunken">
        <div className="flex items-center gap-2">
          <Icon
            name="sliders-horizontal"
            size={14}
            className="text-accent"
          />
          <h3 className="text-md font-semibold text-text-strong">Préférences d'affichage</h3>
        </div>
        <IconButton
          icon="x"
          label="Fermer"
          size="sm"
          onClick={() => setShowTweaks(false)}
        />
      </div>
      <div className="p-4 space-y-4">
        {/* Role switcher — development builds only */}
        {SHOW_DEV_ROLE_SWITCHER && (
        <div>
          <Label>Rôle (dev)</Label>
          <div className="grid grid-cols-2 gap-1">
            {DEV_ROLES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => switchRole(r)}
                className={cn(
                  "h-8 rounded-md text-2xs font-medium border transition-colors",
                  user?.role === r
                    ? "border-accent bg-accent-soft text-accent"
                    : "border-border-default bg-surface text-text-default hover:bg-surface-hover",
                )}
              >
                {(LOCALE_LABELS as Record<string, string>)[r] ?? r}
              </button>
            ))}
          </div>
        </div>
        )}

        {/* Theme */}
        <div>
          <Label>Thème</Label>
          <div className="grid grid-cols-1 gap-1">
            {THEME_OPTIONS.map((t) => (
              <button
                key={t.value}
                type="button"
                aria-pressed={themePref === t.value}
                onClick={() => setTheme(t.value)}
                className={cn(
                  "h-10 px-3 rounded-md text-base font-medium border inline-flex items-center gap-2 transition-colors",
                  themePref === t.value
                    ? "border-accent bg-accent-soft text-accent"
                    : "border-border-default bg-surface text-text-default hover:bg-surface-hover",
                )}
              >
                <Icon name={t.icon} size={14} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Density */}
        <div>
          <Label>Densité</Label>
          <div className="grid grid-cols-2 gap-1">
            {DENSITY_OPTIONS.map((d) => (
              <button
                key={d.value}
                type="button"
                aria-pressed={density === d.value}
                onClick={() => setDensity(d.value)}
                className={cn(
                  "h-10 rounded-md text-base font-medium border transition-colors",
                  density === d.value
                    ? "border-accent bg-accent-soft text-accent"
                    : "border-border-default bg-surface text-text-default hover:bg-surface-hover",
                )}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Menu (ux/01 §6.4, D-21, OP-F-24): hover opening on by default */}
        <div>
          <Label>Menu</Label>
          <Checkbox
            label="Ouvrir les menus au survol"
            checked={navHoverOpen}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNavHoverOpen(e.target.checked)}
          />
        </div>

        {/* Barcode scanner (this workstation) */}
        <div>
          <Label htmlFor="pk-scan-gap">Lecteur de codes-barres</Label>
          <Select
            id="pk-scan-gap"
            options={SCAN_GAP_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
            value={String(scanner.maxGapMs)}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateScanner({ maxGapMs: Number(e.target.value) })}
          />
          <Checkbox
            className="mt-2"
            label="Bip après chaque lecture"
            checked={scanner.beep}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateScanner({ beep: e.target.checked })}
          />
        </div>

        <p className="text-2xs text-text-muted pt-2 border-t border-border-subtle">
          Appliqué immédiatement et enregistré sur ce poste.
        </p>
      </div>
    </div>
  );
};

export default TweaksPanel;
